import { useNavigate } from "react-router-dom";
import { MoreHorizontal, FileDown, FileText, Send, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "./StatusBadge";
import { ReceptorStatusBadge } from "./ReceptorStatusBadge";
import { DTE_TYPE_LABELS } from "../types";
import type { TaxDocumentListItem, DteTypeCode, ReceptorActionStatus } from "../types";

interface TaxDocumentTableProps {
  documents: TaxDocumentListItem[];
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onDownloadXml: (id: string) => void;
  onDownloadPdf?: (id: string) => void;
}

function formatCurrency(value: string | null): string {
  if (!value) return "-";
  const num = parseFloat(value);
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(num);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-CL", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Determine whether to show receptor status badge in the table.
 *
 * We show it when:
 * - Document is accepted by SII (tax authority approval complete)
 * - Receptor status exists and is actionable (not final positive states)
 *
 * Final positive states (goods_received, auto_accepted) are still shown
 * as they provide useful information about commercial acceptance.
 */
function shouldShowReceptorStatus(
  status: string,
  receptorStatus: ReceptorActionStatus | null
): boolean {
  // Only show for SII-accepted documents
  if (status !== "accepted") return false;

  // Don't show if no receptor status
  if (!receptorStatus) return false;

  // Show all receptor statuses for accepted documents
  return true;
}

export function TaxDocumentTable({
  documents,
  selectedIds,
  onSelectionChange,
  onDownloadXml,
  onDownloadPdf,
}: TaxDocumentTableProps) {
  const navigate = useNavigate();

  const allSelected =
    documents.length > 0 && documents.every((d) => selectedIds.has(d.id));

  const someSelected =
    documents.some((d) => selectedIds.has(d.id)) && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(documents.map((d) => d.id)));
    }
  };

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };

  if (documents.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No hay documentos tributarios
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-muted/50">
          <tr>
            <th className="w-10 px-4 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={toggleAll}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium">Tipo</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Folio</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Cliente</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Fecha</th>
            <th className="px-4 py-3 text-right text-sm font-medium">Total</th>
            <th className="px-4 py-3 text-center text-sm font-medium">Estado</th>
            <th className="w-10 px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {documents.map((doc) => (
            <tr
              key={doc.id}
              className="hover:bg-muted/50 cursor-pointer group"
              onClick={() =>
                doc.status === "draft"
                  ? navigate(`/facturacion/emision/nuevo?draftId=${doc.id}`)
                  : navigate(`/facturacion/emision/${doc.id}`)
              }
            >
              <td
                className="px-4 py-3"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(doc.id)}
                  onChange={() => toggleOne(doc.id)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </td>
              <td className="px-4 py-3 text-sm">
                {DTE_TYPE_LABELS[Number(doc.documentTypeCode) as DteTypeCode] ??
                  doc.documentType}
              </td>
              <td className="px-4 py-3 text-sm font-medium">{doc.folio}</td>
              <td className="px-4 py-3 text-sm">
                <div>{doc.recipientName ?? "-"}</div>
                <div className="text-muted-foreground text-xs">
                  {doc.recipientTaxId}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">
                {formatDate(doc.issueDate)}
              </td>
              <td className="px-4 py-3 text-sm text-right font-medium">
                {formatCurrency(doc.totalAmount)}
              </td>
              <td className="px-4 py-3 text-center">
                <div className="flex flex-col items-center gap-1">
                  <StatusBadge status={doc.status} />
                  {shouldShowReceptorStatus(doc.status, doc.receptorStatus) && (
                    <ReceptorStatusBadge
                      status={doc.receptorStatus!}
                      compact
                    />
                  )}
                </div>
              </td>
              <td
                className="px-4 py-3"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() =>
                        navigate(`/facturacion/emision/${doc.id}`)
                      }
                    >
                      Ver detalle
                    </DropdownMenuItem>
                    {onDownloadPdf && (
                      <DropdownMenuItem
                        onClick={() => onDownloadPdf(doc.id)}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Descargar PDF
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => onDownloadXml(doc.id)}
                    >
                      <FileDown className="mr-2 h-4 w-4" />
                      Descargar XML
                    </DropdownMenuItem>
                    {doc.status === "draft" && (
                      <DropdownMenuItem
                        onClick={() =>
                          navigate(`/facturacion/emision/nuevo?draftId=${doc.id}`)
                        }
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Editar borrador
                      </DropdownMenuItem>
                    )}
                    {doc.status === "pending" && (
                      <DropdownMenuItem
                        onClick={() => {
                          onSelectionChange(new Set([doc.id]));
                        }}
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Enviar a SII
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
